import { DialogImportSceneComponent } from "./../../../components/dialog-import-scene/dialog-import-scene.component";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { NgxFlickingComponent, Plugin } from "@egjs/ngx-flicking";
import { Arrow, Sync } from "@egjs/flicking-plugins";
import { Router } from "@angular/router";
import { TranslocoService } from "@ngneat/transloco";
import { MatDialog } from "@angular/material/dialog";
import { ToastrService } from "ngx-toastr";
import { Compbaser } from "../../../components/compbaser";
import { ImportService, LocalStoreService, StoreSelectorsService } from "../../../services";
import { sceneTemplates } from "../../../../assets/accountTemplates/scene-template-constants";
import { IAppStatic, Id, ISceneTemplate } from "../../../interfaces";
import { faCircleChevronLeft, faCircleChevronRight } from "@fortawesome/free-solid-svg-icons";
import * as _ from "lodash";
import { filter } from "rxjs";
import {SceneMimeTypes} from "../../editor/scene-blocks/mime-constants";

interface ITemplateByCategory {
    folder: string;
    count: number;
    plugins: Plugin[];
    items: Array<ISceneTemplate>;
}

interface ICategory {
    folder: string;
    selected: boolean;
}

const firstOrderCategories = ["Presentation"];
@Component({
    selector: "app-home-templates",
    templateUrl: "./templates.component.html",
    styleUrls: ["./templates.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TemplatesComponent extends Compbaser implements OnInit, AfterViewInit {
    @ViewChild("categoriesFlicking0", { static: true }) categoriesFlicking0: NgxFlickingComponent;
    @ViewChild("categoriesFlicking1", { static: true }) categoriesFlicking1: NgxFlickingComponent;

    readonly faArrowLeft = faCircleChevronLeft;
    readonly faArrowRight = faCircleChevronRight;
    readonly largeCategories =  ["Presentation"];

    importModalDiag;
    selectedTemplate: ISceneTemplate;
    appSettings: IAppStatic;

    initialCount = 20;
    moreCount = 10;
    verticalInitialCount = 15;
    verticalMoreCount = 5;

    allTemplatesByCategories: Array<ITemplateByCategory> = [];
    categories: Array<ICategory> = [];
    currentTemplates: Array<ITemplateByCategory> = [];

    // Store full shuffled lists for each category (for lazy loading)
    private shuffledItemsByFolder: { [folder: string]: ISceneTemplate[] } = {};

    throttle = 300;
    scrollDistance = 2;

    flickConfig = {
        gap: 10,
        infinite: true,
        circular: false,
        infiniteThreshold: 100,
        align: "prev",
        // moveType: "freeScroll",
        renderOnlyVisible: false,
        // nested: false
    };

    categoriesPlugins: Plugin[] = [];

    constructor(
        private storeSelector: StoreSelectorsService,
        private localStoreService: LocalStoreService,
        protected override dialog: MatDialog,
        private cd: ChangeDetectorRef
    ) {
        super(dialog);
    }

    /**
     * Shuffle array items randomly using Fisher-Yates algorithm
     */
    private shuffleArray<T>(array: T[]): T[] {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    }

    ngOnInit(): void {
        this.initializeTemplates();

        this.cod(
            this.storeSelector.getAppSettings()
                .subscribe((appSettings: IAppStatic) => {
                    this.appSettings = appSettings;
                })
        );

        this.cod(
            this.storeSelector.getLocalStoreUpdates("ILocalStorageConfig").pipe(filter((v) => v !== null))
                .subscribe((e) => {
                    const selectedCategories = (this.localStoreService.getStorageValConfig("templateCategories") || "").split(";");
                    const selectAll = selectedCategories.length === 1 && selectedCategories[0] === "";
                    this.categories.forEach((v) => {
                        v.selected = selectAll || selectedCategories.includes(v.folder);
                    });
                    this.updateCurrentTemplates();
                })
        );
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.categoriesPlugins = [new Sync({
                type: "camera",
                synchronizedFlickingOptions: [
                    {
                        flicking: this.categoriesFlicking0,
                        isClickable: false
                    },
                    {
                        flicking: this.categoriesFlicking1,
                        isClickable: false
                    },
                ]
            }), new Arrow({ moveCount: 4 })];
        });
    }

    private initializeTemplates() {
        const selectedCategories = (this.localStoreService.getStorageValConfig("templateCategories") || "").split(";");
        const selectAll = selectedCategories.length === 1 && selectedCategories[0] === "";

        // First pass: collect ALL template items into temporary storage
        const allItemsByFolder: { [folder: string]: ISceneTemplate[] } = {};

        for (const sceneName in sceneTemplates) {
            const template = sceneTemplates[sceneName];
            const mimeType = template[3];
            if (mimeType != SceneMimeTypes.Player) {
                continue;
            }

            const folder = template[4].folder;
            if (this.categories.findIndex((v) => v.folder === folder) === -1) {
                this.categories.push({
                    folder,
                    selected: selectAll || selectedCategories.includes(folder)
                });
            }

            const item: ISceneTemplate = this.generateTemplateItem(sceneName, template);

            if (!allItemsByFolder[folder]) {
                allItemsByFolder[folder] = [];
            }
            allItemsByFolder[folder].push(item);
        }

        // Second pass: shuffle all items and populate categories
        for (const folder in allItemsByFolder) {
            const shuffledItems = this.shuffleArray(allItemsByFolder[folder]);
            // Store the full shuffled list for lazy loading
            this.shuffledItemsByFolder[folder] = shuffledItems;
            this.allTemplatesByCategories.push({
                folder: folder,
                count: shuffledItems.length,
                plugins: [new Arrow({ moveCount: 4 })],
                items: shuffledItems.slice(0, this.initialCount)
            });
        }

        // Sort categories by alphabetical
        this.categories.sort((a, b) => {
            if (firstOrderCategories.includes(a.folder)) return -1;
            if (firstOrderCategories.includes(b.folder)) return 1;
            return a.folder.toLowerCase().localeCompare(b.folder.toLowerCase());
        });
        // Shuffle categories after sorting
        this.categories = this.shuffleArray(this.categories);

        // Sort categories templates by alphabetical
        this.allTemplatesByCategories.sort((a, b) => {
            if (firstOrderCategories.includes(a.folder)) return -1;
            if (firstOrderCategories.includes(b.folder)) return 1;
            return a.folder.toLowerCase().localeCompare(b.folder.toLowerCase())
        });

        // Choose initial categories of templates
        this.updateCurrentTemplates();
    }

    /** Load more template items whenever slider slide ends */
    moreTemplateItems(folder) {
        const templates = this.allTemplatesByCategories.find((v) => v.folder == folder);
        if (!templates) return;

        const currentCount = templates.items.length;
        const shuffledList = this.shuffledItemsByFolder[folder];

        if (!shuffledList || currentCount >= shuffledList.length) {
            return;
        }

        // Load next batch from the pre-shuffled list
        const nextBatch = shuffledList.slice(currentCount, currentCount + this.moreCount);
        templates.items.push(...nextBatch);

        this.cd.detectChanges();
    }

    /** Load more template categories when scroll down */
    moreTemplateCategories() {
        const cnt = this.currentTemplates.length;
        const filtered = this.allTemplatesByCategories.filter((v) => {
            return this.categories.findIndex((cate) => cate.folder === v.folder && cate.selected) > -1;
        });
        if (cnt < filtered.length) {
            this.currentTemplates.push(...filtered.slice(cnt, cnt + this.verticalMoreCount));
        }
        this.cd.detectChanges();
    }

    updateCurrentTemplates() {
        // update current templates by selected categories
        this.currentTemplates = this.allTemplatesByCategories.filter((v) => {
            return this.categories.findIndex((cate) => cate.folder === v.folder && cate.selected) > -1;
        }).slice(0, this.verticalInitialCount);

        this.cd.detectChanges();
    }

    private updateCategories() {
        const selectedCategories = this.categories.filter((_cate) => _cate.selected).map((_cate) => _cate.folder).join(";");
        this.localStoreService.setStorageValConfig("templateCategories", selectedCategories || "none");
    }

    selectCategory(category: ICategory) {
        if (!category) return;
        category.selected = !category.selected;
        this.updateCurrentTemplates();
        this.updateCategories();
    }

    onDeselectAll() {
        this.categories.forEach((_cate) => {
            _cate.selected = false;
        });
        this.updateCurrentTemplates();
        this.updateCategories();
    }

    onSelectAll() {
        this.categories.forEach((_cate) => {
            _cate.selected = true;
        });
        this.updateCurrentTemplates();
        this.updateCategories();
    }

    openImportDialog(i_sceneTemplate: ISceneTemplate) {
        if (!i_sceneTemplate) {
            return;
        }
        this.selectedTemplate = i_sceneTemplate;
        this.selectedTemplate.url = `${this.appSettings.cloudResourcesS3}/business1000/resources/scenes_snaps/${this.selectedTemplate.sceneName}.png`;

        if (this.selectedTemplate.folder === "Presentation") {
            this.selectedTemplate.thumbs = this.extractThumbs(this.selectedTemplate.businessId, this.selectedTemplate.childSceneIds);
        }

        this.importModalDiag = this.dialog.open(DialogImportSceneComponent, {
            width: "600px",
            data: {
                template: this.selectedTemplate,
                showDownloadingCancel: true,
                routerAfterImport: true
            },
            disableClose: true
        });

        this.importModalDiag.afterClosed().subscribe(result => {});
    }

    private extractThumbs(businessId, sceneIds) {
        if (!businessId || !sceneIds || sceneIds.split((/[ ,]+/)).length == 0) {
            return [];
        }

        const ids: Id[] = sceneIds.split((/[ ,]+/));
        const thumbs = [];
        Object.keys(sceneTemplates).forEach((sceneName) => {
            const temp = sceneTemplates[sceneName];
            if (temp[0] === businessId && ids.includes(`${temp[2]}`)) {
                const url = `${this.appSettings.cloudResourcesS3}/business1000/resources/scenes_snaps/${sceneName}.png`;
                if (temp[4].folder === "Presentation") {
                    thumbs.unshift(url);
                } else {
                    thumbs.push(url);
                }
            }
        });
        return thumbs;
    }

    private generateTemplateItem(name, template): ISceneTemplate {
        if (!name || !template) return null;
        return {
            nativeId: template[2],
            sceneTitle: template[4].name,
            sceneId: template[2],
            sceneName: name,
            height: "1080",
            width: "1920",
            mimeType: template[3],
            businessId: template[0],
            url: `./assets/images/scenes/${name}.jpg`,
            folder: template[4].folder,
            childSceneIds: template[4].sceneIds
        };
    }
}
